import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/features/auth/AuthContext';
import { ApiError } from '@/lib/api';

export function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  if (isAuthenticated) {
    const next = new URLSearchParams(location.search).get('next') ?? '/';
    return <Navigate to={next} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      const next = new URLSearchParams(location.search).get('next') ?? '/';
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'account_locked') setError('conta bloqueada temporariamente — aguarde.');
        else if (err.code === 'rate_limited') setError('muitas tentativas — tente em alguns minutos.');
        else setError('email ou senha incorretos.');
      } else {
        setError('falha ao entrar — tente novamente.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 p-6">
      <h1 className="text-2xl font-semibold tracking-tight">Entrar</h1>
      <form onSubmit={onSubmit} className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600">Senha</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-slate-900"
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-slate-900 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'entrando…' : 'Entrar'}
        </button>
      </form>
      <button
        type="button"
        onClick={() => setForgotOpen((v) => !v)}
        className="text-sm text-slate-500 underline-offset-2 hover:underline"
      >
        Esqueci minha senha
      </button>
      {forgotOpen && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Peça ao <strong>dono do household</strong> um link de redefinição — ele gera no menu da
          conta ("redefinir senha de membro") e o link vale por 30 minutos. Se o dono é você e não
          lembra a senha, use o procedimento do runbook (<code>reset-password.sh</code>).
        </p>
      )}
    </main>
  );
}
